import api from './api';
import type { InsurerSettlement } from './insurerSettlements';

/**
 * #360: the insurer's payment tracking per broker-month.
 * Calculated (published schedules) / Approved (insurer sign-off) / Paid (the
 * settlements the insurer recorded) / Pending. Amounts are decimal strings.
 */

export type MonthState = 'open' | 'approved' | 'part_paid' | 'paid' | 'disputed';

export const MONTH_STATE_LABEL: Record<MonthState, string> = {
  open: 'Open',
  approved: 'Approved',
  part_paid: 'Part-paid',
  paid: 'Paid',
  disputed: 'Disputed',
};

export interface PaymentRow {
  broker_id: string;
  broker_name: string;
  /** First day of the month, YYYY-MM-01. */
  month: string;
  calculated: string;
  approved: string | null;
  paid: string;
  pending: string | null;
  on_account: string;
  state: MonthState;
  /** Calculated now minus the figure frozen at approval; null before approval. */
  variance: string | null;
  unpriced_policies: number;
  paid_before_approval: boolean;
}

export interface PaymentKpis {
  calculated: string;
  approved: string;
  paid: string;
  pending: string;
}

export interface PaymentDetail extends PaymentRow {
  settlements: InsurerSettlement[];
  approved_at: string | null;
  approval_reason: string;
  adjustments: { previous: string; approved: string; reason: string; at: string }[];
}

export interface PaymentFilters {
  broker?: string;
  month_from?: string;
  month_to?: string;
  state?: MonthState | '';
}

const clean = (f: PaymentFilters = {}) => Object.fromEntries(Object.entries(f).filter(([, v]) => v));
const monthKey = (month: string) => month.slice(0, 7);

export const insurerPaymentsService = {
  async list(filters?: PaymentFilters): Promise<{ kpis: PaymentKpis; count: number; results: PaymentRow[] }> {
    return (await api.get('/commissions/insurer-payments/', { params: clean(filters) })).data;
  },
  async detail(brokerId: string, month: string): Promise<PaymentDetail> {
    return (await api.get(`/commissions/insurer-payments/${brokerId}/${monthKey(month)}/`)).data;
  },
  async approve(brokerId: string, month: string, body: { amount?: string; reason?: string }): Promise<PaymentDetail> {
    return (await api.post(`/commissions/insurer-payments/${brokerId}/${monthKey(month)}/approve/`, body)).data;
  },
  async exportCsv(filters?: PaymentFilters): Promise<Blob> {
    return (await api.get('/commissions/insurer-payments/export/', { params: clean(filters), responseType: 'blob' })).data;
  },
};
